import {
  DeviceRow,
  epochSeconds,
  KeycloakDevice,
  KeycloakSession,
} from "core/types"

/** Keycloak's own DeviceRepresentation.BROWSER_VERSION_SEPARATOR. */
const BROWSER_VERSION_SEPARATOR = "/"

/** Keycloak's placeholder when it cannot identify a device or browser. */
const UNKNOWN = "Other"

const OS_DISPLAY_NAMES: Record<string, string> = {
  "Mac OS X": "macOS",
  "Mac OS": "macOS",
  iOS: "iOS",
  "Windows Phone": "Windows Phone",
}

/**
 * Keycloak reports raw user-agent OS strings like "Mac OS X". Prefer the OS
 * name over `device`, which is a hardware family ("Mac", "iPhone").
 */
export const deviceDisplayName = (device: KeycloakDevice): string => {
  const os = device.os?.trim()

  if (!os || os === UNKNOWN) {
    const hardware = device.device?.trim()
    return hardware && hardware !== UNKNOWN ? hardware : UNKNOWN
  }

  return OS_DISPLAY_NAMES[os] ?? os
}

/** Strips the version Keycloak appends, turning "Chrome/153.0.0" into "Chrome". */
export const browserName = (browser: string | undefined): string => {
  const name = browser?.split(BROWSER_VERSION_SEPARATOR)[0]?.trim()
  return name ? name : UNKNOWN
}

const toDeviceRow = (
  device: KeycloakDevice,
  session: KeycloakSession
): DeviceRow => ({
  sessionId: session.id,
  name: deviceDisplayName(device),
  ipAddress: session.ipAddress ?? "",
  browser: browserName(session.browser),
  isCurrent: session.current === true,
  lastAccess: epochSeconds(session.lastAccess),
})

/**
 * Flattens Keycloak's device buckets into one row per session, current first.
 *
 * Keycloak builds the devices payload by concatenating the online and offline
 * session streams, so the same session id can appear more than once; those
 * duplicates are dropped because a single DELETE already logs out both.
 */
export const toDeviceRows = (devices: KeycloakDevice[]): DeviceRow[] => {
  const seen = new Set<string>()
  const rows: DeviceRow[] = []

  devices.forEach((device) => {
    device.sessions?.forEach((session) => {
      if (seen.has(session.id)) return

      seen.add(session.id)
      rows.push(toDeviceRow(device, session))
    })
  })

  const current = rows.filter((row) => row.isCurrent)
  const rest = rows.filter((row) => !row.isCurrent)

  return [...current, ...rest]
}
